/**
 * AdminPage.tsx — the /admin console (ADMIN-gated).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY: the admin console is a clinic-scoped workspace navigated by the shared
 * LEFT SIDEBAR (ui/SideNav — UX Wave 3a, 2026-07-21: the horizontal tab strip
 * became an Agency-Accelerator-style persistent vertical menu; the SAME
 * component drives /clinicians). Sections: Pipeline (the default), Plan
 * Management (price CRUD), Users (staff directory + invite), Payments (UX
 * Wave 3b: the ONE money section — Card view money board ⇄ List view
 * subscriptions table with the failed-payments/refund and
 * no-subscription-yet filters folded in), Insights (UX Wave 3c: the merged
 * Analytics + Earnings section — Company analytics / Earnings sub-views under
 * ONE shared window selector), Pharmacy (G4, 2026-09-13 — the operational
 * surface for orders already out with Pharmacy Express, which is where the
 * unvouched-order recall lives) and the Audit log (rebuilt as the Wave-3c
 * back-tracer). Form requests (assessment fill-on-behalf-of-patient +
 * plan-link handover) MOVED to the clinician workspace 2026-07-20 — admin no
 * longer fills forms on a patient's behalf. UX Wave 5 (2026-07-21 eve)
 * rebuilt the shell around the full-height staff RAIL (ui/SideNav) + compact
 * top bar; UX Wave 7B (2026-07-21 late) polished it to analytics parity:
 * the waffle ClinicSwitcher tops the rail, the section items wear
 * outline-SVG icons, and the GPS logo sits on the WHITE masthead block
 * (ui/StaffMasthead — workspace name beside it, the per-section breadcrumb
 * "Admin console › <Section>" under it) next to the shared ProfileMenu; the
 * emergency ribbon, centred-logo header and legal footer no longer render on
 * staff routes (see ui/chrome.tsx's AppShell).
 * The working pane is FULL-WIDTH fluid (no 1100px frame) — the pipeline
 * board gets the whole viewport. This page just reads the active clinic back
 * from `?clinic=` (defaulting to Weight Loss) so the rail switcher and the
 * page body stay in lock-step via the URL. The ACTIVE SECTION is likewise
 * URL-synced (`?tab=` — the KEYS ARE UNCHANGED from the tab era, so every
 * existing deep link and bookmark still lands on the right section; the
 * RETIRED `alerts` key redirects into the merged Payments section, the
 * retired `analytics`/`earnings` keys redirect into the merged Insights
 * section — `earnings` also pre-selects its sub-view via `?view=` — and
 * `?clinic=` is preserved when switching). Only Weight Loss is configured
 * today — any other clinic replaces the section area with the friendly
 * "coming soon" panel; the RAIL ITSELF STAYS (Wave 5 — the waffle switcher
 * lives on it, so the way back to Weight Loss is always in reach) but its
 * section items are hidden for a non-live clinic. The role gate is applied at
 * the route (RequireRole); the gateway is the real control (R41).
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProfileMenu } from '../ui/chrome';
import { IconAudit, IconDispensing, IconEngagementContent, IconInsights, IconNotify, IconPatients, IconPayments, IconPipeline, IconPlans, IconUsers } from '../ui/icons';
import { SideNav, SideNavLayout, type SideNavItem } from '../ui/SideNav';
import { StaffMasthead } from '../ui/StaffMasthead';
import { AuditLogTab } from './AuditLogTab';
import { ClinicSwitcher } from './ClinicSwitcher';
import { DEFAULT_CLINIC_SLUG, findClinic } from './clinics';
import { ComingSoonPanel } from './ComingSoonPanel';
import { InsightsSection } from './InsightsSection';
import { PlanManagement } from './PlanManagement';
import { UsersTab } from './UsersTab';
import { PipelineTab } from '../pipeline/PipelineTab';
import { PatientsTab } from '../pms/PatientsTab';
import { PaymentsSection } from './PaymentsSection';
import { NotifyRequestsTab } from './NotifyRequestsTab';
import { PharmacyOpsTab } from './PharmacyOpsTab';
import { EngagementContentTab } from './EngagementContentTab';

type Tab = 'plans' | 'engagement' | 'users' | 'patients' | 'pipeline' | 'orders' | 'pharmacy' | 'insights' | 'audit' | 'notify';

/** CTO priority order (2026-07-21 CRM redesign): the PIPELINE leads — the one
 * HubSpot-style board both roles work; the old Callbacks tab folded into it
 * (its ticketing drawer lives on inside the deal drawer). `orders` keeps its
 * key (deep links survive) and now reads plain "Payments" — UX Wave 3b merged
 * the old "Payments pipeline" and "Payments" items into this ONE section
 * (card/list toggle; the failed-payments feed, recent ledger and refund fold
 * into its list view). `insights` is the Wave-3c merge of the old Analytics +
 * Earnings items (§4) — sub-views under one shared window selector. The
 * Audit log (G2) sits LAST — a governance read, not a daily working surface.
 * Icons (Wave 5 — the rail is icons + labels, icon-only when collapsed;
 * Wave 7B §1 — OUTLINE-ONLY inline SVGs in `currentColor`, the emoji are
 * gone) are decorative; the label always carries the accessible name. */
const TABS: Array<SideNavItem<Tab>> = [
  { key: 'pipeline', label: 'Pipeline', icon: <IconPipeline /> },
  // Wave 6 §5: the PMS patient directory sits right after the pipeline — the
  // HubSpot Contacts-next-to-Deals pairing (the Payments drawer deep-links
  // into it: `?tab=patients&patient=<id>`).
  { key: 'patients', label: 'Patients', icon: <IconPatients /> },
  { key: 'plans', label: 'Plan management', icon: <IconPlans /> },
  // The engagement content library (2026-09-23, CTO-approved ContentManager
  // board) — the PDF guides the re-engagement emails and WhatsApp unlock
  // links serve. It sits under Plan management because both answer "what can
  // we offer a patient", one in money and one in words.
  { key: 'engagement', label: 'Engagement content', icon: <IconEngagementContent /> },
  { key: 'users', label: 'Users', icon: <IconUsers /> },
  { key: 'orders', label: 'Payments', icon: <IconPayments /> },
  // G4 (2026-09-13) — the PHARMACY section. Every pharmacy-facing control this
  // console had was per-order and buried in the pipeline deal drawer, reachable
  // only by someone who already knew which patient the pharmacy was ringing
  // about. The unvouched-order recall is a SWEEP, not a per-order act, so it had
  // nowhere honest to live: Payments is money and this moves none, Pipeline is
  // one deal at a time, Audit is a read. It sits next to Payments because both
  // are the downstream-of-approval operational lanes staff work daily.
  { key: 'pharmacy', label: 'Pharmacy', icon: <IconDispensing /> },
  { key: 'insights', label: 'Insights', icon: <IconInsights /> },
  { key: 'notify', label: 'Notify requests', icon: <IconNotify /> },
  { key: 'audit', label: 'Audit log', icon: <IconAudit /> },
];

const DEFAULT_TAB: Tab = 'pipeline';

/**
 * The sections that are NOT clinic-scoped, and are therefore the only ones the
 * rail keeps when the selected clinic is not live.
 *
 * `notify` because those requests come from pages whose service is not a clinic
 * yet. `pharmacy` because the recall route it hosts takes no clinic and sweeps
 * the whole tenant — filing it under `?clinic=` would imply a narrowing the
 * server does not perform, which is the kind of quiet untruth this batch exists
 * to remove. `engagement` for the same reason as `pharmacy`: the content
 * library's routes take no clinic and its guides serve every unlock link the
 * tenant mints.
 */
const GLOBAL_TABS = new Set<Tab>(['notify', 'pharmacy', 'engagement']);

/**
 * Retired tab keys → the section that absorbed them (Wave 3b: the old
 * "Payments" tab's `alerts` key lands on the merged Payments section;
 * Wave 3c: `analytics`/`earnings` land on the merged Insights section), so
 * every pre-merge deep link and bookmark still works.
 */
const LEGACY_TABS: Record<string, Tab> = { alerts: 'orders', analytics: 'insights', earnings: 'insights' };

function isTab(value: string | null): value is Tab {
  return TABS.some((t) => t.key === value);
}

export function AdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const clinicSlug = searchParams.get('clinic') ?? DEFAULT_CLINIC_SLUG;
  // The active tab lives in the URL (`?tab=`) — a retired key maps to the
  // section that absorbed it, and an unknown/absent value falls back to the
  // default so a stale deep link never renders a blank tab area.
  const tabParam = searchParams.get('tab');
  const tab: Tab = isTab(tabParam) ? tabParam : LEGACY_TABS[tabParam ?? ''] ?? DEFAULT_TAB;
  const clinic = findClinic(clinicSlug);
  const configured = clinic?.configured ?? false;
  const globalTab = GLOBAL_TABS.has(tab);

  // Rewrite a retired key in the URL itself (replace — no history entry), so
  // copied links from here on carry the canonical key. The retired `earnings`
  // key also pins the Insights sub-view (`?view=earnings`) so the old deep
  // link still lands on the money table, not just the section.
  useEffect(() => {
    if (!tabParam || !LEGACY_TABS[tabParam]) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', LEGACY_TABS[tabParam]);
    if (tabParam === 'earnings') next.set('view', 'earnings');
    setSearchParams(next, { replace: true });
  }, [tabParam, searchParams, setSearchParams]);

  function selectTab(key: Tab) {
    // Copy the existing params so `?clinic=` (and anything else) survives.
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  }

  // The breadcrumb's current crumb (Wave 7B §8): the active section's label
  // on a live clinic; the clinic name itself while its coming-soon panel shows.
  const sectionLabel = configured || globalTab
    ? TABS.find((t) => t.key === tab)?.label ?? 'Pipeline'
    : clinic?.name ?? clinicSlug;

  return (
    <SideNavLayout
      nav={
        <SideNav
          label="Admin sections"
          // A non-live clinic hides the section items but keeps the rail —
          // the waffle switcher on it is the way back to Weight Loss.
          items={configured ? TABS : TABS.filter((item) => GLOBAL_TABS.has(item.key))}
          activeKey={tab}
          onSelect={selectTab}
          switcher={<ClinicSwitcher />}
        />
      }
      // Wave 7B: the logo left the green rail for the WHITE masthead block —
      // workspace name beside it, the per-section breadcrumb under it. The
      // old in-content "Admin console" page-title block is gone (§6).
      masthead={
        <StaffMasthead
          workspace="Admin console"
          section={sectionLabel}
          homeTo="/admin"
          homeLabel="GP Service — admin home"
        />
      }
      account={<ProfileMenu />}
    >
      {globalTab ? (
        tab === 'notify' ? <NotifyRequestsTab /> : tab === 'engagement' ? <EngagementContentTab /> : <PharmacyOpsTab />
      ) : !configured ? (
        <ComingSoonPanel clinicName={clinic?.name ?? clinicSlug} audience="admin" />
      ) : (
        <>
          {tab === 'plans' ? <PlanManagement key={clinicSlug} clinicSlug={clinicSlug} /> : null}
          {tab === 'users' ? <UsersTab /> : null}
          {tab === 'patients' ? <PatientsTab variant="admin" /> : null}
          {tab === 'pipeline' ? <PipelineTab /> : null}
          {tab === 'orders' ? <PaymentsSection key={clinicSlug} clinicSlug={clinicSlug} /> : null}
          {tab === 'insights' ? <InsightsSection /> : null}
          {tab === 'audit' ? <AuditLogTab /> : null}
        </>
      )}
    </SideNavLayout>
  );
}
